import { getFirebaseAuth } from './firebase';

const CATALOGO_SAT_API_URL = process.env.REACT_APP_CATALOGO_SAT_API_URL || '/api/catalogo-sat';
const MAX_RESULTS = 20;

export class CatalogoSatApiError extends Error {
  constructor(message, { status = 0, code = 'CATALOGO_SAT_ERROR', kind = 'network', retryAfterSeconds = null } = {}) {
    super(message);
    this.name = 'CatalogoSatApiError';
    this.status = status;
    this.code = code;
    this.kind = kind;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

function classifyStatus(status) {
  if (status === 401) return 'auth';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  if (status === 429) return 'rate_limit';
  if (status >= 500) return 'server';
  return 'validation';
}

function resolveEndpoint(path) {
  return `${String(CATALOGO_SAT_API_URL).replace(/\/+$/, '')}/${String(path).replace(/^\/+/, '')}`;
}

async function resolveToken() {
  try {
    const user = getFirebaseAuth().currentUser;
    if (!user) {
      throw new CatalogoSatApiError('Tu sesión terminó. Inicia sesión nuevamente.', {
        status: 401,
        code: 'AUTH_REQUIRED',
        kind: 'auth'
      });
    }
    return await user.getIdToken();
  } catch (error) {
    if (error instanceof CatalogoSatApiError) throw error;
    throw new CatalogoSatApiError('No fue posible obtener la sesión de Firebase.', {
      status: 401,
      code: 'AUTH_UNAVAILABLE',
      kind: 'auth'
    });
  }
}

async function parseResponse(response) {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    throw new CatalogoSatApiError('El servidor devolvió una respuesta inválida.', {
      status: response.status,
      code: 'INVALID_RESPONSE',
      kind: 'server'
    });
  }
}

async function request(path, { method = 'GET', body, signal } = {}) {
  const token = await resolveToken();
  let response;
  try {
    response = await fetch(resolveEndpoint(path), {
      method,
      signal,
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
        ...(body ? { 'Content-Type': 'application/json' } : {})
      },
      ...(body ? { body: JSON.stringify(body) } : {})
    });
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    throw new CatalogoSatApiError('No fue posible conectar con el Catálogo SAT.', {
      code: 'NETWORK_ERROR',
      kind: 'network'
    });
  }
  const data = await parseResponse(response);
  if (!response.ok) {
    throw new CatalogoSatApiError(data?.message || data?.error || `HTTP ${response.status}`, {
      status: response.status,
      code: data?.code || data?.error || `HTTP_${response.status}`,
      kind: classifyStatus(response.status),
      retryAfterSeconds: Number.isFinite(Number(data?.retryAfterSeconds)) ? Number(data.retryAfterSeconds) : null
    });
  }
  return data;
}

function normalizeCapabilities(value) {
  return {
    canManageCommonKeys: value?.canManageCommonKeys === true
  };
}

function normalizeListResponse(data) {
  return {
    items: Array.isArray(data?.items) ? data.items.slice(0, MAX_RESULTS) : [],
    nextCursor: typeof data?.nextCursor === 'string' ? data.nextCursor : null,
    capabilities: normalizeCapabilities(data?.capabilities)
  };
}

export function isCatalogoSatQueryValid(value) {
  const query = String(value || '').trim();
  return /^\d{8}$/.test(query) || query.length >= 3;
}

export async function searchCatalogoSat({ query, soloClase = false, clienteId, soloComunes = false, limit = MAX_RESULTS, signal } = {}) {
  const normalizedQuery = String(query || '').trim();
  if (!isCatalogoSatQueryValid(normalizedQuery)) {
    throw new CatalogoSatApiError('Escribe al menos 3 caracteres o una clave exacta de 8 dígitos.', {
      status: 400,
      code: 'INVALID_QUERY',
      kind: 'validation'
    });
  }
  const params = new URLSearchParams({
    q: normalizedQuery,
    soloClase: String(soloClase !== false),
    limit: String(Math.min(Math.max(Number(limit) || MAX_RESULTS, 1), MAX_RESULTS))
  });
  if (clienteId) params.set('clienteId', clienteId);
  if (soloComunes) params.set('soloComunes', 'true');
  return normalizeListResponse(await request(`/v1/claves?${params.toString()}`, { signal }));
}

export async function searchCatalogoSatClients({ query, signal } = {}) {
  const normalizedQuery = String(query || '').trim();
  if (normalizedQuery.length < 3) {
    throw new CatalogoSatApiError('Escribe al menos 3 caracteres para buscar clientes.', {
      status: 400,
      code: 'INVALID_CLIENT_QUERY',
      kind: 'validation'
    });
  }
  return normalizeListResponse(await request(`/v1/clientes?q=${encodeURIComponent(normalizedQuery)}`, { signal }));
}

export async function getCatalogoSatCommonKeys({ clientId, signal } = {}) {
  if (!clientId) throw new CatalogoSatApiError('Selecciona un cliente.', { status: 400, code: 'CLIENT_REQUIRED', kind: 'validation' });
  return normalizeListResponse(await request(`/v1/clientes/${encodeURIComponent(clientId)}/claves-comunes`, { signal }));
}

export async function addCatalogoSatCommonKey({ clientId, claveSat, signal } = {}) {
  return request(`/v1/clientes/${encodeURIComponent(clientId)}/claves-comunes`, {
    method: 'POST',
    body: { claveSat },
    signal
  });
}

export async function removeCatalogoSatCommonKey({ clientId, claveSat, signal } = {}) {
  return request(`/v1/clientes/${encodeURIComponent(clientId)}/claves-comunes/${encodeURIComponent(claveSat)}`, {
    method: 'DELETE',
    signal
  });
}
